import baseApi from './base.api'
import {
    normalizeBuildQuestionDefinitionsResponse,
    normalizeImportRubricResponse,
    normalizePattern,
    normalizeQuestionRubric,
    normalizeQuestionRubricsResponse,
    normalizeRubricProgressResponse,
    normalizeRubricWithPatterns,
    normalizeRubricsResponse,
} from './utils/normalizers/rubric.normalizers'
import { onFetchRubricProgressQueryStarted } from './utils/response-handlers/rubric.api.response-handlers'
import { createSearchParams } from '../../utils/request.helper'
import { createTagsForList, QUESTION_RUBRIC_TAG, QUESTION_TAG, RUBRIC_TAG } from './utils/tags'

export const rubricApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        // ============ Legacy Import Endpoints ============
        importRubric: builder.mutation({
            query: ({ file }) => {
                const formData = new FormData()
                formData.append('file', file)

                return {
                    url: `/text-models/parse-rubrics-file`,
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: normalizeImportRubricResponse,
        }),
        buildQuestionDefinitions: builder.mutation({
            query: ({ filledQuestionSchema, indexOfFilledQuestion, unmatchedValuesPerQuestion }) => {
                const unmatchedValuesPerQuestionMap = unmatchedValuesPerQuestion.reduce((acc, value, index) => {
                    acc[`Q${index + 1}`] = value.join('\n')
                    return acc
                }, {})

                const initialUnmatchedValuesOfFilledQuestion =
                    unmatchedValuesPerQuestion[indexOfFilledQuestion].join('\n')

                const normalizedFilledQuestionSchema = {
                    name: filledQuestionSchema.name,
                    text: filledQuestionSchema.content,
                    grading_reference_content: filledQuestionSchema.gradingReferenceContent,
                    weight: filledQuestionSchema.weight,
                    word_limit: filledQuestionSchema.wordsLimit,
                    categories: filledQuestionSchema.categories,
                }

                return {
                    url: `/text-models/build-question-definitions`,
                    method: 'POST',
                    body: {
                        example_json: normalizedFilledQuestionSchema,
                        example_rubric: initialUnmatchedValuesOfFilledQuestion,
                        questions_to_parse: unmatchedValuesPerQuestionMap,
                    },
                }
            },
            transformResponse: normalizeBuildQuestionDefinitionsResponse,
        }),

        // ============ Rubric CRUD Endpoints ============
        
        /**
         * Fetch all rubrics (with patterns) for the current context
         */
        fetchRubrics: builder.query({
            query: ({ token }) => ({
                url: `/rubrics/${token}`,
                method: 'GET',
            }),
            transformResponse: normalizeRubricsResponse,
            providesTags: result => createTagsForList(result, RUBRIC_TAG),
        }),

        /**
         * Fetch a single rubric by ID (with patterns)
         */
        fetchRubric: builder.query({
            query: ({ rubricId }) => ({
                url: `/rubrics/${rubricId}`,
                method: 'GET',
            }),
            transformResponse: normalizeRubricWithPatterns,
            providesTags: (result, error, { rubricId }) => [{ type: RUBRIC_TAG, id: rubricId }],
        }),

        /**
         * Create a new rubric
         */
        createRubric: builder.mutation({
            query: ({ token, name, content, gradingReferenceContent, weight }) => ({
                url: `/rubrics/${token}`,
                method: 'POST',
                body: { 
                    name, 
                    content, 
                    grading_reference_content: gradingReferenceContent, 
                    weight 
                },
            }),
            transformResponse: normalizeRubricWithPatterns,
            invalidatesTags: [{ type: RUBRIC_TAG, id: 'LIST' }],
        }),

        /**
         * Create a rubric with patterns in one request
         */
        createRubricWithPatterns: builder.mutation({
            query: ({ name, content, gradingReferenceContent, patterns }) => ({
                url: `/rubrics/with-patterns`,
                method: 'POST',
                body: {
                    name,
                    content,
                    grading_reference_content: gradingReferenceContent,
                    patterns: patterns?.map(pattern => ({
                        name: pattern.name,
                        description: pattern.description,
                        philosophy_text: pattern.philosophyText,
                        feedback: pattern.feedback,
                        grade: pattern.grade,
                        color_id: pattern.colorId,
                        order: pattern.order,
                        type: pattern.type,
                    })),
                },
            }),
            transformResponse: normalizeRubricWithPatterns,
            invalidatesTags: [{ type: RUBRIC_TAG, id: 'LIST' }],
        }),

        /**
         * Update a rubric
         */
        updateRubric: builder.mutation({
            query: ({ rubricId, name, content, gradingReferenceContent, weight }) => ({
                url: `/rubrics/${rubricId}`,
                method: 'PUT',
                body: { 
                    name, 
                    content, 
                    grading_reference_content: gradingReferenceContent, 
                    weight 
                },
            }),
            transformResponse: normalizeRubricWithPatterns,
            invalidatesTags: (result, error, { rubricId }) => [
                { type: RUBRIC_TAG, id: rubricId },
                { type: RUBRIC_TAG, id: 'LIST' },
            ],
        }),

        /**
         * Delete a rubric
         */
        deleteRubric: builder.mutation({
            query: ({ rubricId }) => ({
                url: `/rubrics/${rubricId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { rubricId }) => [
                { type: RUBRIC_TAG, id: rubricId },
                { type: RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),

        // ============ Category Management within Rubrics ============

        /**
         * Add a category to a rubric
         */
        addCategoryToRubric: builder.mutation({
            query: ({ rubricId, name, description, philosophyText, feedback, grade, colorId, order, type, conceptualLabels, autoApprove }) => ({
                url: `/rubrics/${rubricId}/patterns`,
                method: 'POST',
                body: {
                    name,
                    description,
                    philosophy_text: philosophyText,
                    feedback,
                    grade,
                    color_id: colorId,
                    order,
                    type,
                    conceptual_labels: conceptualLabels,
                    auto_approve: autoApprove !== undefined ? autoApprove : true,
                },
            }),
            transformResponse: normalizePattern,
            invalidatesTags: (result, error, { rubricId }) => [
                { type: RUBRIC_TAG, id: rubricId },
                { type: RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),

        /**
         * Fetch categories for a rubric
         */
        fetchRubricCategories: builder.query({
            query: ({ rubricId }) => ({
                url: `/rubrics/${rubricId}/patterns`,
                method: 'GET',
            }),
            transformResponse: response => (response || []).map(normalizePattern),
            providesTags: (result, error, { rubricId }) => [{ type: RUBRIC_TAG, id: rubricId }],
        }),

        /**
         * Delete a category from a rubric
         */
        deleteCategoryFromRubric: builder.mutation({
            query: ({ rubricId, categoryId }) => ({
                url: `/rubrics/${rubricId}/patterns/${categoryId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { rubricId }) => [
                { type: RUBRIC_TAG, id: rubricId },
                { type: RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),

        /**
         * Reorder categories within a rubric
         */
        reorderRubricCategories: builder.mutation({
            query: ({ rubricId, idToOrderMap }) => ({
                url: `/rubrics/${rubricId}/patterns/reorder`,
                method: 'PATCH',
                body: idToOrderMap,
            }),
            invalidatesTags: (result, error, { rubricId }) => [
                { type: RUBRIC_TAG, id: rubricId },
                { type: RUBRIC_TAG, id: 'LIST' },
            ],
        }),

        // ============ Question-Rubric Association Endpoints ============

        /**
         * Assign a rubric to a question
         */
        assignRubricToQuestion: builder.mutation({
            query: ({ questionId, rubricId, position = 0 }) => ({
                url: `/questions/${questionId}/rubrics`,
                method: 'POST',
                params: { rubric_id: rubricId, position },
            }),
            transformResponse: normalizeQuestionRubric,
            invalidatesTags: (result, error, { questionId }) => [
                { type: QUESTION_RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: questionId },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),

        /**
         * Fetch rubrics assigned to a question
         */
        fetchQuestionRubrics: builder.query({
            query: ({ questionId }) => ({
                url: `/questions/${questionId}/rubrics`,
                method: 'GET',
            }),
            transformResponse: normalizeQuestionRubricsResponse,
            providesTags: (result, error, { questionId }) => [
                { type: QUESTION_RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: questionId },
            ],
        }),

        /**
         * Remove a rubric association from a question
         */
        removeRubricFromQuestion: builder.mutation({
            query: ({ questionRubricId }) => ({
                url: `/questions/question-rubrics/${questionRubricId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { questionRubricId }) => [
                { type: QUESTION_RUBRIC_TAG, id: questionRubricId },
                { type: QUESTION_RUBRIC_TAG, id: 'LIST' },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),

        // ============ Training & Progress Endpoints ============

        /**
         * Get processing progress for question rubrics
         * Also updates the questions cache with the new progress data
         */
        fetchRubricProgress: builder.query({
            query: ({ questionRubricIds }) => ({
                url: `/rubrics/processing-progress`,
                method: 'GET',
                params: createSearchParams({ question_rubric_id: questionRubricIds }),
            }),
            transformResponse: normalizeRubricProgressResponse,
            onQueryStarted: onFetchRubricProgressQueryStarted,
        }),

        /**
         * Train a question rubric association
         */
        trainQuestionRubric: builder.mutation({
            query: ({ questionRubricId, trainingMode }) => ({
                url: `/rubrics/question-rubrics/${questionRubricId}/train`,
                method: 'POST',
                params: trainingMode !== undefined ? { training_mode: trainingMode } : undefined,
            }),
            invalidatesTags: (result, error, { questionRubricId }) => [
                { type: QUESTION_RUBRIC_TAG, id: questionRubricId },
                { type: QUESTION_TAG, id: 'LIST' },
            ],
        }),
    }),
})

export const {
    // Legacy
    useImportRubricMutation,
    useBuildQuestionDefinitionsMutation,
    // Rubric CRUD
    useFetchRubricsQuery,
    useLazyFetchRubricsQuery,
    useFetchRubricQuery,
    useLazyFetchRubricQuery,
    useCreateRubricMutation,
    useCreateRubricWithPatternsMutation,
    useUpdateRubricMutation,
    useDeleteRubricMutation,
    // Category management within rubrics
    useAddCategoryToRubricMutation,
    useFetchRubricCategoriesQuery,
    useDeleteCategoryFromRubricMutation,
    useReorderRubricCategoriesMutation,
    // Question-Rubric associations
    useAssignRubricToQuestionMutation,
    useFetchQuestionRubricsQuery,
    useRemoveRubricFromQuestionMutation,
    // Training & Progress
    useFetchRubricProgressQuery,
    useLazyFetchRubricProgressQuery,
    useTrainQuestionRubricMutation,
} = rubricApi
